import React from 'react';
import { ExternalLink, FileText, Lightbulb, ListChecks } from 'lucide-react';
import { Link } from 'react-router-dom';
import GuideAccountCTA from '../components/GuideAccountCTA';
import './ApplicationTimelinePage.css';

const topTips = [
  'Finish as much of your main essay as possible during the summer before senior year. Once school starts, classes, activities, and deadlines stack up quickly.',
  'Start supplements earlier than feels necessary, especially reusable prompts like why major, why us, community, identity, and activity essays.',
  'Ask for recommendations early and give teachers and counselors plenty of time.',
  'Keep one final college list and one deadline tracker so deadlines, portals, essays, and financial aid do not live in five different places.',
  'Submit before the final night when possible. Portals, payments, uploads, and school forms can take longer than expected.',
  'Check portals after submission because submitted is not always the same as complete.',
  'Complete FAFSA as soon as it opens, and submit CSS Profile by the earliest priority filing date if a school requires it.',
];

const timelineSections = [
  {
    title: 'Junior Spring',
    items: [
      'Build a broad college list with reaches, targets, safeties, costs, locations, and programs you actually like.',
      'Take or plan SAT/ACT testing if scores could help your list, scholarships, or specific programs.',
      'Ask your counselor how recommendation letters, transcripts, and school forms work at your school.',
      'Consider asking teachers about recommendations before summer if your school culture supports it.',
      'Start activity, honors, awards, course, and testing notes in My Info while details are still fresh.',
    ],
  },
  {
    title: 'Senior Summer',
    items: [
      'Draft your Common App personal essay early and get feedback from people who know your voice.',
      'Research colleges and narrow which schools might be ED, EA, ED II, rolling, or regular decision.',
      'Register for a final SAT/ACT attempt if needed, leaving enough time for scores to return and be sent.',
      'Start supplemental essay ideas, especially prompts that can be reused across schools.',
      'Create Common App, College Board/ACT, FAFSA, CSS Profile, and other major account logins.',
      'Enjoy the summer too. The goal is to reduce panic, not turn every day into application homework.',
    ],
  },
  {
    title: 'August',
    items: [
      'Add colleges to Common App so you can see each school’s requirements, writing supplements, testing policy, recommendation needs, and school-specific questions.',
      'Build your Final College List in Progress once you know the schools you are actively applying to.',
      'Confirm application deadlines, scholarship deadlines, financial aid priority dates, CSS Profile requirements, recommendation requirements, and portal steps.',
      'Make a first pass at all ED/EA supplements before school work gets heavy.',
    ],
  },
  {
    title: 'September - October',
    items: [
      'Ask teachers for recommendations if you have not already done so, and follow your school’s transcript/recommendation request process.',
      'Finish ED/EA personal essay and supplement drafts.',
      'Take a final fall SAT/ACT only if scores can arrive before the schools that need them.',
      'Prepare FAFSA/CSS materials with your family, including tax and income documents.',
      'Watch college scholarship deadlines; some merit scholarship deadlines arrive before or near application deadlines.',
    ],
  },
  {
    title: 'November',
    items: [
      'Many ED/EA deadlines fall around November 1 or November 15, but every school is different. Double-check each college and portal.',
      'Submit ED/EA applications early enough to avoid portal, payment, or upload issues.',
      'Confirm counselor forms, teacher recommendations, transcripts, and required test scores are submitted or in progress.',
      'After each submission, check the portal for missing items and follow-up tasks.',
    ],
  },
  {
    title: 'December',
    items: [
      'Complete regular decision essays and keep polishing the schools still left on your list.',
      'Watch for ED/EA decisions and read each decision letter carefully.',
      'If accepted ED, follow the college’s instructions for deposits, withdrawals, and next steps.',
      'Submit scholarship applications with December deadlines.',
      'Send official SAT/ACT scores where required, and confirm whether each school accepts self-reported scores first.',
    ],
  },
  {
    title: 'January - February',
    items: [
      'Submit regular decision and ED II applications.',
      'Submit FAFSA/CSS Profile by each school’s priority date.',
      'Handle interviews if offered, and keep checking portals after each application is submitted.',
      'If deferred, prepare a Letter of Continued Interest only if the college accepts one.',
      'Start external scholarship applications; many continue into spring.',
    ],
  },
  {
    title: 'March - April',
    items: [
      'Track regular decision results, scholarship notifications, and financial aid offers.',
      'Visit admitted-student events if possible, virtually or in person.',
      'Compare cost, fit, program strength, location, support, and the day-to-day life you want.',
      'If waitlisted, decide whether to accept the waitlist spot and send a LOCI only where appropriate.',
    ],
  },
  {
    title: 'May',
    items: [
      'Commit by the national enrollment deadline if you are not already committed through ED.',
      'Pay the deposit or confirm your deposit waiver.',
      'Send your final transcript and complete housing, orientation, placement, and advising steps.',
      'Take a breath. You made it through the main application season.',
    ],
  },
];

const sources = [
  { label: 'Common App first-year guide', url: 'https://www.commonapp.org/apply/first-year-students/' },
  { label: 'FAFSA', url: 'https://studentaid.gov/h/apply-for-aid/fafsa' },
  { label: 'FAFSA deadlines', url: 'https://studentaid.gov/apply-for-aid/fafsa/fafsa-deadlines' },
  { label: 'CSS Profile getting started', url: 'https://cssprofile.collegeboard.org/getting-started' },
  { label: 'SAT dates and deadlines', url: 'https://satsuite.collegeboard.org/sat/dates-deadlines' },
  { label: 'Sending SAT scores', url: 'https://satsuite.collegeboard.org/scores/sending-sat-scores' },
  { label: 'ACT test dates', url: 'https://www.act.org/content/act/en/products-and-services/the-act/registration/test-dates.html' },
  { label: 'Sending ACT scores', url: 'https://www.act.org/content/act/en/products-and-services/the-act/scores/sending-your-scores.html' },
];

function ApplicationTimelinePage() {
  return (
    <main className="timeline-page">
      <div className="timeline-page__inner">
        <header className="timeline-page__hero">
          <span className="timeline-page__eyebrow">Application Timeline</span>
          <h1>Suggested college application timeline</h1>
          <p>
            This is a general roadmap to keep you on track from junior spring through enrollment.
            It is not an official deadline list. Once you log into Common App and add colleges,
            you will see each school’s specific requirements, deadlines, writing supplements,
            recommendation needs, testing policies, and school-specific questions.
          </p>
        </header>

        <section className="timeline-page__intro" aria-label="Before you start">
          <div className="timeline-page__intro-icon">
            <FileText size={19} strokeWidth={1.8} />
          </div>
          <div>
            <h2>Log in early, then confirm everything</h2>
            <p>
              Many colleges require counselor forms, transcripts, and teacher letters of recommendation.
              Requirements vary by school, so creating accounts and adding colleges early makes the
              process less mysterious before deadlines get close.
            </p>
          </div>
        </section>

        <section className="timeline-page__tips" aria-labelledby="tips-title">
          <div className="timeline-page__section-heading">
            <Lightbulb size={18} strokeWidth={1.8} />
            <h2 id="tips-title">Top tips</h2>
          </div>
          <div className="timeline-page__tip-grid">
            {topTips.map((tip) => (
              <article className="timeline-tip" key={tip}>
                <ListChecks size={16} strokeWidth={1.9} />
                <p>{tip}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="timeline-page__note timeline-page__note--action" aria-label="Use this timeline in MaiColleges">
          <span className="timeline-page__action-kicker">Put this plan into MaiColleges</span>
          <h2>Turn the guide into dates, reminders, and real next steps</h2>
          <p>
            Use <strong>General Progress</strong> for application-wide work such as recommendations,
            your main essay, FAFSA, CSS Profile, and score sends. Its suggested dates are editable and
            appear in Calendar and reminders. Use <strong>Individual Progress</strong> for each college’s
            exact deadline, supplements, portal checks, and submitted status.
          </p>
          <div className="timeline-page__action-row">
            <Link className="timeline-page__internal-link" to="/progress?tab=general">Open General Progress</Link>
            <Link className="timeline-page__internal-link timeline-page__internal-link--secondary" to="/progress?tab=individual">Open Individual Progress</Link>
          </div>
        </section>

        <section className="timeline-page__timeline" aria-labelledby="timeline-title">
          <div className="timeline-page__section-heading">
            <ListChecks size={18} strokeWidth={1.8} />
            <h2 id="timeline-title">Month-by-month guide</h2>
          </div>
          <div className="timeline-list">
            {timelineSections.map((section) => (
              <article className="timeline-block" key={section.title}>
                <div className="timeline-block__marker" aria-hidden="true" />
                <div className="timeline-block__content">
                  <h3>{section.title}</h3>
                  <ul>
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="timeline-page__note" aria-label="Always confirm">
          <h2>Always confirm with each college</h2>
          <p>
            Deadlines, testing policies, financial aid forms, scholarship requirements, interviews,
            portfolios, portals, and recommendation rules can change by college and by program. Use this
            page as your planning map, then verify the final details in Common App, the college portal,
            and the college’s official admissions and financial aid pages.
          </p>
        </section>

        <section className="timeline-page__sources" aria-labelledby="sources-title">
          <h2 id="sources-title">Helpful official sources</h2>
          <div className="timeline-source-grid">
            {sources.map((source) => (
              <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>
                <span>{source.label}</span>
                <ExternalLink size={14} strokeWidth={1.9} />
              </a>
            ))}
          </div>
        </section>

        <GuideAccountCTA title="Turn this summer plan into your first dashboard" />
      </div>
    </main>
  );
}

export default ApplicationTimelinePage;
